import { useState, useEffect } from 'react';
import {
  Settings,
  Trash2,
  Download,
  User,
  Bell,
  Globe,
  AlertTriangle,
  Smartphone,
  X
} from 'lucide-react';
import { checkSessionExpired } from '../lib/api';
import { CareerProfileSection } from '../components/CareerProfileSection';
import { InterestProfileSection } from '../components/InterestProfileSection';
import { KarrierepassetSection } from '../components/KarrierepassetSection';

interface Profile {
  id: string;
  full_name: string;
  date_of_birth: string;
  language: 'no' | 'en';
  notification_preferences: {
    email: boolean;
    sms: boolean;
  };
  status: 'active' | 'pending_deletion';
  deletion_scheduled_for: string | null;
  deletion_requested_at: string | null;
  created_at: string;
  last_login_at: string;
  login_count: number;
}

export function SettingsPage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Remember token state
  const [hasRememberToken, setHasRememberToken] = useState(false);
  const [rememberTokenExpires, setRememberTokenExpires] = useState<string | null>(null);
  const [clearingToken, setClearingToken] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchRememberTokenStatus();
  }, []);

  async function fetchProfile() {
    try {
      setLoading(true);
      const res = await fetch('/api/candidate/profile', { credentials: 'include' });
      await checkSessionExpired(res);
      if (!res.ok) throw new Error('Kunne ikke hente profil');
      const data = await res.json();
      setProfile(data.profile);
    } catch (err) {
      console.error('Fetch profile error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchRememberTokenStatus() {
    try {
      const res = await fetch('/api/candidate/auth/remember-token/status', { credentials: 'include' });
      await checkSessionExpired(res);
      if (res.ok) {
        const data = await res.json();
        setHasRememberToken(data.has_remember_token);
        setRememberTokenExpires(data.expires_at);
      }
    } catch (err) {
      console.error('Fetch remember token status error:', err);
    }
  }

  async function handleClearRememberToken() {
    try {
      setClearingToken(true);
      const res = await fetch('/api/candidate/auth/remember-token/clear', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      await checkSessionExpired(res);
      if (!res.ok) throw new Error('Kunne ikke fjerne husket enhet');

      // Clear local storage
      localStorage.removeItem('kandidat_remember_token');
      localStorage.removeItem('kandidat_profile_id');

      setHasRememberToken(false);
      setRememberTokenExpires(null);
      alert('Enheten er glemt. Du må logge inn med passord neste gang.');
    } catch (err: any) {
      alert('Feil: ' + err.message);
    } finally {
      setClearingToken(false);
    }
  }

  async function handleLanguageChange(language: 'no' | 'en') {
    try {
      setSaving(true);
      const res = await fetch('/api/candidate/profile/language', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language })
      });

      await checkSessionExpired(res);
      if (!res.ok) throw new Error('Kunne ikke oppdatere språk');

      setProfile((prev) => prev ? { ...prev, language } : null);
    } catch (err: any) {
      alert('Feil: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleNotificationChange(type: 'email' | 'sms', enabled: boolean) {
    if (!profile) return;

    try {
      setSaving(true);
      const updatedPrefs = {
        ...profile.notification_preferences,
        [type]: enabled
      };

      const res = await fetch('/api/candidate/profile/notifications', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_preferences: updatedPrefs })
      });

      await checkSessionExpired(res);
      if (!res.ok) throw new Error('Kunne ikke oppdatere varsler');

      setProfile((prev) => prev ? { ...prev, notification_preferences: updatedPrefs } : null);
    } catch (err: any) {
      alert('Feil: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleExportData() {
    try {
      const res = await fetch('/api/candidate/profile/export', { credentials: 'include' });
      await checkSessionExpired(res);
      if (!res.ok) throw new Error('Kunne ikke eksportere data');

      const data = await res.json();
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mine-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert('Kunne ikke eksportere data: ' + err.message);
    }
  }

  async function handleDeleteAccount() {
    try {
      setDeleteLoading(true);
      const res = await fetch('/api/candidate/profile/delete', {
        method: 'POST',
        credentials: 'include'
      });

      await checkSessionExpired(res);
      if (!res.ok) throw new Error('Kunne ikke slette konto');

      const data = await res.json();
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              status: 'pending_deletion',
              deletion_scheduled_for: data.deletion_scheduled_for,
              deletion_requested_at: new Date().toISOString()
            }
          : null
      );
      setShowDeleteModal(false);
      alert(
        `Konto merket for sletting. Du har ${data.grace_period_days} dager på å angre før kontoen slettes permanent.`
      );
    } catch (err: any) {
      alert('Feil: ' + err.message);
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleCancelDeletion() {
    try {
      setCancelLoading(true);
      const res = await fetch('/api/candidate/profile/cancel-deletion', {
        method: 'POST',
        credentials: 'include'
      });

      await checkSessionExpired(res);
      if (!res.ok) throw new Error('Kunne ikke avbryte sletting');

      setProfile((prev) =>
        prev
          ? {
              ...prev,
              status: 'active',
              deletion_scheduled_for: null,
              deletion_requested_at: null
            }
          : null
      );
      alert('Sletting avbrutt. Kontoen din er aktiv igjen.');
    } catch (err: any) {
      alert('Feil: ' + err.message);
    } finally {
      setCancelLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-orange mx-auto"></div>
          <p className="mt-4 text-kf-gray-600">Laster innstillinger...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card text-center py-12">
          <AlertTriangle className="w-16 h-16 text-kf-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-kf-gray-900 mb-2">Kunne ikke laste profil</h2>
          <p className="text-kf-gray-600">Vennligst prøv igjen senere.</p>
        </div>
      </div>
    );
  }

  const daysUntilDeletion = profile.deletion_scheduled_for
    ? Math.ceil(
        (new Date(profile.deletion_scheduled_for).getTime() - new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold text-kf-gray-900">Innstillinger</h1>

      {/* Pending Deletion Warning */}
      {profile.status === 'pending_deletion' && profile.deletion_scheduled_for && (
        <div className="card bg-kf-error-50 border-kf-error-300">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-kf-error-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-kf-error-900 mb-2">Konto merket for sletting</h3>
              <p className="text-kf-error-800 mb-4">
                Din konto vil bli permanent slettet om{' '}
                <strong className="font-bold">{daysUntilDeletion} dager</strong> (
                {new Date(profile.deletion_scheduled_for).toLocaleDateString('no-NO', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                ).
              </p>
              <button
                onClick={handleCancelDeletion}
                disabled={cancelLoading}
                className="bg-kf-error-600 text-white hover:bg-kf-error-700 px-6 py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                {cancelLoading ? 'Avbryter...' : 'Avbryt sletting'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Karriereprofil */}
      <CareerProfileSection />
      <InterestProfileSection />

      <KarrierepassetSection />

      {/* Profile Information */}
      <div className="card">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Profilinformasjon
        </h2>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-kf-gray-700">Navn</p>
            <p className="text-kf-gray-900">{profile.full_name}</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700">Fødselsdato</p>
            <p className="text-kf-gray-900">
              {new Date(profile.date_of_birth).toLocaleDateString('no-NO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700">Medlem siden</p>
            <p className="text-kf-gray-900">
              {new Date(profile.created_at).toLocaleDateString('no-NO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700">Sist innlogget</p>
            <p className="text-kf-gray-900">
              {new Date(profile.last_login_at).toLocaleDateString('no-NO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-kf-gray-700">Antall innlogginger</p>
            <p className="text-kf-gray-900">{profile.login_count}</p>
          </div>
        </div>
      </div>

      {/* Language Settings */}
      <div className="card">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Språk
        </h2>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="language"
              value="no"
              checked={profile.language === 'no'}
              onChange={() => handleLanguageChange('no')}
              disabled={saving}
              className="w-4 h-4 text-kf-orange-600 focus:ring-kf-orange"
            />
            <span className="text-kf-gray-900">Norsk</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="language"
              value="en"
              checked={profile.language === 'en'}
              onChange={() => handleLanguageChange('en')}
              disabled={saving}
              className="w-4 h-4 text-kf-orange-600 focus:ring-kf-orange"
            />
            <span className="text-kf-gray-900">English</span>
          </label>
        </div>
        {saving && <p className="text-sm text-kf-gray-600 mt-2">Lagrer...</p>}
      </div>

      {/* Notification Settings */}
      <div className="card">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Varsler
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-kf-gray-900">E-postvarsler</p>
              <p className="text-sm text-kf-gray-600">Motta varsler om nye attester via e-post</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={profile.notification_preferences.email}
                onChange={(e) => handleNotificationChange('email', e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-kf-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-kf-orange/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-kf-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-kf-orange-600"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-kf-gray-900">SMS-varsler</p>
              <p className="text-sm text-kf-gray-600">Motta varsler om nye attester via SMS</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={profile.notification_preferences.sms}
                onChange={(e) => handleNotificationChange('sms', e.target.checked)}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-kf-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-kf-orange/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-kf-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-kf-orange-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Remembered Devices */}
      <div className="card">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          Huskede enheter
        </h2>
        <div className="space-y-4">
          <p className="text-sm text-kf-gray-700">
            Når du velger "Husk meg" ved innlogging, lagres en sikker nøkkel på denne enheten
            slik at du slipper å taste passord hver gang. Du kan fjerne denne nøkkelen her.
          </p>

          {hasRememberToken ? (
            <div className="bg-kf-gray-50 border border-kf-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-kf-orange-100 rounded-full flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-kf-orange-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-kf-gray-900">Denne enheten</p>
                    <p className="text-sm text-kf-gray-600">
                      Gyldig til {rememberTokenExpires ? new Date(rememberTokenExpires).toLocaleDateString('no-NO', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'ukjent dato'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClearRememberToken}
                  disabled={clearingToken}
                  className="text-kf-error-600 hover:text-kf-error-700 p-2 rounded-lg hover:bg-kf-error-50 transition-colors disabled:opacity-50"
                  title="Glem denne enheten"
                >
                  {clearingToken ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-kf-error-600 border-t-transparent"></div>
                  ) : (
                    <X className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-kf-gray-50 border border-kf-gray-200 rounded-lg p-4 text-center">
              <p className="text-kf-gray-600">
                Ingen enheter er husket. Velg "Husk meg" ved neste innlogging for å aktivere.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* GDPR Actions */}
      <div className="card">
        <h2 className="text-xl font-bold text-kf-gray-900 mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Datahåndtering (GDPR)
        </h2>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-kf-gray-700 mb-3">
              I henhold til GDPR har du rett til å eksportere og slette dine personopplysninger.
            </p>
            <button
              onClick={handleExportData}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Eksporter mine data (JSON)
            </button>
          </div>

          {profile.status === 'active' && (
            <div className="pt-4 border-t border-kf-gray-200">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="bg-kf-error-600 text-white hover:bg-kf-error-700 w-full py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Slett konto permanent
              </button>
              <p className="text-xs text-kf-gray-600 mt-2 text-center">
                Dette vil starte en 30-dagers venteperiode før permanent sletting
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-kf-error-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-kf-gray-900 mb-2">Bekreft kontosletting</h3>
                <p className="text-sm text-kf-gray-700 mb-4">
                  Er du sikker på at du vil slette kontoen din? Denne handlingen vil:
                </p>
                <ul className="list-disc list-inside text-sm text-kf-gray-700 space-y-1 mb-4">
                  <li>Merke kontoen din for sletting</li>
                  <li>Gi deg 30 dager på å angre</li>
                  <li>Permanent slette alle dine data etter 30 dager</li>
                  <li>Fjerne tilgang til alle dine attester</li>
                </ul>
                <p className="text-sm font-semibold text-kf-error-700">
                  Du vil motta en epost med bekreftelse og instruks for å angre.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                className="flex-1 btn-secondary"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                className="flex-1 bg-kf-error-600 text-white hover:bg-kf-error-700 py-2 px-4 rounded-lg font-semibold disabled:opacity-50"
              >
                {deleteLoading ? 'Sletter...' : 'Ja, slett konto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
